import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { BuyCdnTrafficModal } from './BuyCdnTrafficModal';
import type { Subscription } from '../../types/subscription';
import type { CdnQuota } from '../../types/cdn';
import { cdnBarPercent, cdnQuotaState } from '../../utils/cdnQuota';
import { CDN_TITLE, CDN_TOPUP_BUTTON, cdnQuotaHint, cdnUsageLine } from '../../texts/cdn';
import styles from './SubscriptionStatus.module.css';

interface SubscriptionStatusProps {
  subscription: Subscription | null;
  /** Квота обхода белых списков; null или hasAccess=false — блок не рендерим */
  cdnQuota?: CdnQuota | null;
  /** После успешной докупки трафика (stub-режим) — родитель перезапрашивает данные */
  onTopupPurchased?: () => void;
}

const statusVariant = {
  active: 'success' as const,
  expired: 'error' as const,
  cancelled: 'error' as const,
  pending: 'warning' as const,
};

const statusLabel = {
  active: 'Активна',
  expired: 'Истекла',
  cancelled: 'Отменена',
  pending: 'Ожидание',
};

export function SubscriptionStatus({
  subscription,
  cdnQuota = null,
  onTopupPurchased,
}: SubscriptionStatusProps) {
  const navigate = useNavigate();
  const [topupOpen, setTopupOpen] = useState(false);

  if (!subscription) {
    return (
      <div className={styles.card}>
        <div className={styles.noSub}>
          <div className={styles.noSubText}>Нет активной подписки</div>
          <Button onClick={() => navigate('/dashboard/plans')}>
            Выбрать тариф
          </Button>
        </div>
      </div>
    );
  }

  const expiresAt = new Date(subscription.expiresAt);
  const now = new Date();
  const daysLeft = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));

  const startsAt = new Date(subscription.startsAt);
  const totalDays = Math.ceil((expiresAt.getTime() - startsAt.getTime()) / (1000 * 60 * 60 * 24));
  const elapsed = totalDays - daysLeft;
  const progress = totalDays > 0 ? Math.min(100, (elapsed / totalDays) * 100) : 0;

  const formatDate = (date: Date) =>
    date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

  // Блок обхода белых списков: только когда квота есть и доступ открыт
  const quota = cdnQuota && cdnQuota.hasAccess ? cdnQuota : null;
  const quotaState = quota ? cdnQuotaState(quota) : 'ok';
  const quotaTextClass =
    quotaState === 'exhausted' ? styles.stateError : quotaState === 'warning' ? styles.stateWarning : '';
  const quotaFillClass =
    quotaState === 'exhausted' ? styles.fillError : quotaState === 'warning' ? styles.fillWarning : '';
  const quotaPercent = quota ? cdnBarPercent(quota) : 0;

  return (
    <div className={styles.card}>
      <div className={styles.row}>
        <div className={styles.left}>
          <span className={styles.title}>Подписка</span>
          <Badge variant={statusVariant[subscription.status]}>
            {statusLabel[subscription.status]}
          </Badge>
        </div>
        <div className={styles.right}>
          <span className={styles.expiresAt}>до {formatDate(expiresAt)}</span>
          <span className={`${styles.daysLeft} ${daysLeft <= 7 ? styles.daysLeftWarning : ''}`}>
            {daysLeft > 0 ? `Осталось ${daysLeft} дн.` : 'Срок истёк'}
          </span>
          {daysLeft <= 7 && subscription.status === 'active' && (
            <Button size="sm" onClick={() => navigate('/dashboard/plans')}>
              Продлить
            </Button>
          )}
        </div>
      </div>
      <div className={styles.progressBar}>
        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
      </div>

      {quota && (
        <div className={styles.cdnBlock}>
          <div className={styles.cdnRow}>
            <span className={styles.cdnTitle}>{CDN_TITLE}</span>
            <div className={styles.cdnRight}>
              <span className={`${styles.cdnUsage} ${quotaTextClass}`}>{cdnUsageLine(quota)}</span>
              {quota.topupAvailable && (
                <Button
                  size="sm"
                  variant={quotaState === 'exhausted' ? 'primary' : 'secondary'}
                  onClick={() => setTopupOpen(true)}
                >
                  {CDN_TOPUP_BUTTON}
                </Button>
              )}
            </div>
          </div>
          <div
            className={styles.progressBar}
            role="progressbar"
            aria-label={CDN_TITLE}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(quotaPercent)}
          >
            <div
              className={`${styles.progressFill} ${quotaFillClass}`}
              style={{ width: `${quotaPercent}%` }}
            />
          </div>
          <p className={`${styles.cdnHint} ${quotaTextClass}`}>{cdnQuotaHint(quota)}</p>
        </div>
      )}

      {quota?.topupAvailable && (
        <BuyCdnTrafficModal
          isOpen={topupOpen}
          onClose={() => setTopupOpen(false)}
          onPurchased={onTopupPurchased}
        />
      )}
    </div>
  );
}
